// components/notifications/NotificationsModal.tsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, ChevronDown, ChevronUp, Search, Bell } from 'lucide-react'
import { Button, Text } from '@mantine/core'
import { toast } from 'react-hot-toast'
import moment from 'moment'
import { NotificationsModalSkeleton } from '../ui/Skeleton'
import {
  useNotificationsByView,
  useArchiveNotification,
  useMarkAsSeen,
  useMarkNotificationAsReadWithVersion,
} from '@/hooks/api/useNotifications'
import {
  NotificationSummary,
  NotificationView,
  TeamFeedResponse,
  MyWorkResponse,
  ArchiveResponse,
} from '@/types/api/notifications'

const globalSeenNotifications = new Set<string>()
const globalSeenInProgress = new Set<string>()

interface NotificationsModalProps {
  notifications: NotificationSummary[]
  isLoading: boolean
  onClose: () => void
  onRefresh: () => void
}

interface GroupedNotification {
  data_id: string
  notifications: NotificationSummary[]
  hasUnread: boolean
  timestamp: string
  isSeen?: boolean
  isDeferred?: boolean
  deferredUntil?: number | null
}

const transformThreadLikeToNotification = (
  threadLike: any
): NotificationSummary => {
  const latest = threadLike.latest_notification || {}

  const notification: NotificationSummary = {
    id: latest.notification_id || threadLike.thread_id || '',
    title: latest.title || 'Notification',
    message: latest.body || '',
    status: latest.lifecycle_state === 'unread' ? 'unread' : 'read',
    priority: latest.priority || '',
    created_at: latest.createdAt || new Date().toISOString(),
    updated_by_name: latest.actor_name || 'System',
    related_entity_id: threadLike.case_id || latest.data_id || '',
    type: latest.data_type || 'update',
    action_url: latest.action_url || undefined,
    read_at: latest.read_at || undefined,
  }

  const notificationWithData = notification as any
  notificationWithData._thread = threadLike
  notificationWithData._notification = latest
  notificationWithData.version = latest.version || 1
  notificationWithData.notification_id =
    latest.notification_id || threadLike.thread_id || ''
  notificationWithData.lifecycle_state = latest.lifecycle_state || 'unread'
  notificationWithData.priority = latest.priority || ''
  notificationWithData.data_type = latest.data_type || 'update'
  notificationWithData.data_group = latest.data_group || ''
  notificationWithData.classification = latest.classification || ''
  notificationWithData.placement = latest.placement || ''
  notificationWithData.scope_level = latest.scope_level || ''
  notificationWithData.stage = latest.stage || ''
  notificationWithData.recipient_entity = latest.recipient_entity || ''
  notificationWithData.requires_action = latest.requires_action || false
  notificationWithData.actor_name = latest.actor_name || 'System'
  notificationWithData.recipient_id = latest.recipient_id || ''
  notificationWithData.precinct_id = latest.precinct_id || ''
  notificationWithData.server_timestamp = latest.server_timestamp || Date.now()
  notificationWithData.payload = latest.payload || {}
  notificationWithData.case_id = threadLike.case_id || latest.data_id || ''
  notificationWithData.data_id = latest.data_id || threadLike.case_id || ''
  notificationWithData.action = latest.action || 'unseen'

  notificationWithData.isDeferred =
    latest.lifecycle_state === 'deferred' ||
    (latest.deferred_until && latest.deferred_until > Date.now())
  notificationWithData.deferredUntil = latest.deferred_until || null

  return notification
}

const groupNotifications = (
  notifications: NotificationSummary[]
): GroupedNotification[] => {
  if (!notifications || notifications.length === 0) return []

  const grouped = notifications.reduce(
    (acc, notification) => {
      const notificationWithData = notification as any

      let groupKey = notification.related_entity_id || notification.id

      if (notificationWithData.case_id?.startsWith('shift-')) {
        groupKey = notificationWithData.case_id
      } else if (notificationWithData.data_id?.startsWith('shift-')) {
        groupKey = notificationWithData.data_id
      } else if (notificationWithData.case_id) {
        groupKey = notificationWithData.case_id
      } else if (notificationWithData.data_id) {
        groupKey = notificationWithData.data_id
      }

      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(notification)
      return acc
    },
    {} as Record<string, NotificationSummary[]>
  )

  return Object.entries(grouped).map(([data_id, notifications]) => {
    const isDeferred = notifications.some((n: any) => {
      const deferredUntil = n.deferredUntil || (n as any).deferred_until
      const isDeferredStatus = (n as any).lifecycle_state === 'deferred'
      const isExpired = deferredUntil && deferredUntil < Date.now()

      return (
        isDeferredStatus ||
        (deferredUntil && deferredUntil > Date.now() && !isExpired)
      )
    })

    const allSeen = notifications.every((n: any) => n.action === 'seen')

    return {
      data_id,
      notifications,
      hasUnread: notifications.some(n => n.status === 'unread'),
      timestamp: notifications[0]?.created_at || '',
      isSeen: allSeen,
      isDeferred: isDeferred,
      deferredUntil: notifications.reduce((latest, n: any) => {
        const until = n.deferredUntil || n.deferred_until
        if (until && (!latest || until > latest)) return until
        return latest
      }, null),
    }
  })
}

function isTeamFeedResponse(data: any): data is TeamFeedResponse {
  return data?.data?.threads !== undefined
}

function isMyWorkResponse(data: any): data is MyWorkResponse {
  return (
    data?.data?.active_cases !== undefined ||
    data?.data?.shift_alerts !== undefined ||
    data?.data?.requires_action !== undefined ||
    data?.data?.informational !== undefined
  )
}

function isArchiveResponse(data: any): data is ArchiveResponse {
  return (
    data?.data?.notifications !== undefined && data?.data?.threads === undefined
  )
}

const getPriorityConfig = (priority?: string) => {
  switch (priority?.toLowerCase()) {
    case 'high':
      return {
        label: 'High',
        dot: 'bg-[#FA5252]',
        border: 'border-[#FA5252]',
        bg: 'bg-[#FFF5F5]',
        seenBg: 'bg-[#FFF5F5]/50',
      }
    case 'medium':
      return {
        label: 'Medium',
        dot: 'bg-[#FF922B]',
        border: 'border-[#FF922B]',
        bg: 'bg-[#FFF4E6]',
        seenBg: 'bg-[#FFF4E6]/50',
      }
    case 'low':
      return {
        label: 'Low',
        dot: 'bg-gray-400',
        border: 'border-gray-200',
        bg: 'bg-gray-50',
        seenBg: 'bg-gray-50/50',
      }
    default:
      return {
        label: 'Low',
        dot: 'bg-gray-400',
        border: 'border-gray-200',
        bg: 'bg-gray-50',
        seenBg: 'bg-gray-50/50',
      }
  }
}

const NotificationItem: React.FC<{
  group: GroupedNotification
  isExpanded: boolean
  isMarkingRead: boolean
  isDeferred: boolean
  isSeen: boolean
  isProcessing: boolean
  onToggleExpand: (id: string) => void
  onArchive: (
    group: GroupedNotification,
    notification: NotificationSummary
  ) => void
  onViewDetails: (group: GroupedNotification) => void
  onInView: (group: GroupedNotification) => void
  activeTab: string
}> = ({
  group,
  isExpanded,
  isMarkingRead,
  isDeferred,
  isSeen,
  isProcessing,
  onToggleExpand,
  onArchive,
  onViewDetails,
  onInView,
  activeTab,
}) => {
  const latest = group.notifications[0]
  const priority = getPriorityConfig(latest?.priority)
  const notificationRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!notificationRef.current || !group.hasUnread) return

    const notificationWithData = latest as any
    const notificationId =
      notificationWithData.notification_id || latest?.id || ''
    const version = notificationWithData.version || 1
    const requestKey = `${notificationId}-${version}`

    if (
      globalSeenNotifications.has(notificationId) ||
      globalSeenInProgress.has(requestKey)
    ) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && group.hasUnread) {
            if (
              globalSeenNotifications.has(notificationId) ||
              globalSeenInProgress.has(requestKey)
            ) {
              return
            }

            if (timeoutId) {
              clearTimeout(timeoutId)
              timeoutId = null
            }

            timeoutId = setTimeout(() => {
              if (
                !globalSeenNotifications.has(notificationId) &&
                !globalSeenInProgress.has(requestKey)
              ) {
                globalSeenInProgress.add(requestKey)
                onInView(group)
              }
              timeoutId = null
            }, 1500)
          } else {
            if (timeoutId) {
              clearTimeout(timeoutId)
              timeoutId = null
            }
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(notificationRef.current)

    return () => {
      observer.disconnect()
      if (timeoutId) {
        clearTimeout(timeoutId)
        timeoutId = null
      }
    }
  }, [group.hasUnread, group, onInView, latest])

  const formatCaseId = (case_id: string, createdAt: string) => {
    if (!case_id || !createdAt) return '—'
    const date = new Date(createdAt)
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, '')
    const suffix = case_id.slice(-4).toUpperCase()
    return `C${formattedDate}-${suffix}`
  }

  return (
    <div
      ref={notificationRef}
      className={`p-1 mb-4 rounded-xl border border-[#D7D7D7] transition-shadow ${
        isProcessing && activeTab !== 'archive'
          ? 'opacity-50 pointer-events-none'
          : ''
      } ${
        isSeen ? 'bg-white hover:shadow-md' : 'bg-[#EBF5FF] hover:shadow-md'
      }`}
    >
      <div className="p-5">
        <div className="flex justify-between items-start">
          <h3 className="font-bold text-[18px] text-[#23476B]">
            {formatCaseId(group.data_id, group.timestamp)}
          </h3>

          <div className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${priority.dot}`} />
            <span className="text-[14px]">{priority.label}</span>

            {isDeferred && (
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                Deferred{' '}
                {group.deferredUntil
                  ? `until ${moment(group.deferredUntil).format('h:mm A')}`
                  : '✓'}
              </span>
            )}
            {/* {!isSeen && !isDeferred && (
              <span className="ml-2 w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            )} */}
          </div>
        </div>
      </div>

      <p className="mt-2 text-[15px] leading-7 text-[#2E2E2E] line-clamp-2">
        {latest?.message}
      </p>

      {/* Activity Bar */}
      <div
        onClick={() => onToggleExpand(group.data_id)}
        className={`mt-4 overflow-hidden rounded-lg border ${priority.border} ${priority.bg}
cursor-pointer transition-colors`}
      >
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase text-[#6A6A6A]">updated by</span>
            <div className="w-6 h-6 rounded-full bg-[#24476B] text-white flex items-center justify-center text-[11px]">
              {(latest?.updated_by_name || 'S')[0]}
            </div>
            <span className="font-medium text-sm">
              {latest?.updated_by_name || 'System'}
            </span>
            <span className="text-sm text-[#7A7A7A]">
              {moment(new Date(latest?.created_at || '')).format('h:mm A')}
            </span>
          </div>
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>

        {/* Timeline */}
        {isExpanded && (
          <div className="border-t border-black/5 px-6 py-5">
            <div className="relative ml-4 border-l border-[#D7D7D7]">
              {group.notifications.map((n, index) => (
                <div
                  key={n.id}
                  className={`relative pl-6 ${
                    index < group.notifications.length - 1 ? 'pb-6' : ''
                  }`}
                >
                  <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-[3px] border-[#24476B]" />
                  <div className="flex gap-2 flex-wrap text-sm">
                    <span className="font-semibold">{n.title}</span>
                    <span className="text-[#7A7A7A]">
                      {moment(new Date(n.created_at)).format(
                        'MMM D, YYYY h:mm A'
                      )}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-[#5D5E67]">{n.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-4 mt-5">
        {activeTab !== 'archive' && (
          <button
            disabled={isMarkingRead || isProcessing || isDeferred}
            onClick={() => {
              onArchive(group, latest)
            }}
            className={`h-[42px] px-6 rounded-lg border font-medium transition-all ${
              isMarkingRead || isProcessing || isDeferred
                ? 'border-[#E5E7EB] bg-[#F8F8F8] text-[#BDBDBD] cursor-not-allowed'
                : 'border-[#D0D5DD] bg-white text-[#2E2E2E] hover:bg-[#F7F7F7]'
            }`}
          >
            {isMarkingRead ? 'Archiving...' : 'Archive'}
          </button>
        )}

        <Button
          onClick={() => onViewDetails(group)}
          styles={{
            root: {
              background: '#000',
              color: '#fff',
              height: 42,
              borderRadius: 8,
              paddingLeft: 22,
              paddingRight: 22,
              '&:hover': {
                background: '#333',
              },
            },
          }}
        >
          View Details
        </Button>
      </div>
    </div>
  )
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  notifications: _initialNotifications,
  isLoading: initialLoading,
  onClose,
  onRefresh,
}) => {
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<NotificationView>('team_feed')
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const [markingAsRead, setMarkingAsRead] = useState<Record<string, boolean>>(
    {}
  )
  const [search, setSearch] = useState('')
  const [isClosing, setIsClosing] = useState(false)
  const [processingGroups] = useState<Record<string, boolean>>({})
  const [groupedNotificationsState, setGroupedNotifications] = useState<
    GroupedNotification[]
  >([])
  const [lastKey, setLastKey] = useState<any>(null)

  const processingRef = useRef<Set<string>>(new Set())
  const { data, isLoading, isFetching, refetch } = useNotificationsByView(
    activeTab,
    50,
    lastKey
  )
  const archiveMutation = useArchiveNotification()
  const markAsSeenMutation = useMarkAsSeen()
  const markAsReadMutation = useMarkNotificationAsReadWithVersion()

  const hasMore = useMemo(() => {
    return data?.data?.last_key !== null && data?.data?.last_key !== undefined
  }, [data])

  const loadMore = useCallback(() => {
    if (isFetching || !hasMore || isLoading) {
      return
    }

    const nextLastKey = data?.data?.last_key
    if (nextLastKey) {
      setLastKey(nextLastKey)
    }
  }, [isFetching, hasMore, data, isLoading])

  useEffect(() => {
    setLastKey(null)
    setExpandedGroups({})
  }, [activeTab])

  const observerRef = useRef<IntersectionObserver | null>(null)
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetching) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver(
        entries => {
          if (
            entries[0].isIntersecting &&
            hasMore &&
            !isFetching &&
            !isLoading
          ) {
            loadMore()
          }
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0.1,
        }
      )

      if (node) observerRef.current.observe(node)
    },
    [isFetching, hasMore, loadMore, isLoading]
  )

  const notifications = useMemo(() => {
    if (!data?.data) return []

    if (isTeamFeedResponse(data)) {
      if (data.data.threads && data.data.threads.length > 0) {
        return data.data.threads.map(transformThreadLikeToNotification)
      }
      return data.data.notifications ?? []
    }

    if (isMyWorkResponse(data)) {
      const responseData = data.data as any

      if (responseData.threads && responseData.threads.length > 0) {
        return responseData.threads.map(transformThreadLikeToNotification)
      }

      const allNotifications: NotificationSummary[] = []

      if (responseData.active_cases?.length > 0) {
        if (responseData.active_cases[0]?.latest_notification) {
          const transformed = responseData.active_cases.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.active_cases)
        }
      }

      if (responseData.shift_alerts?.length > 0) {
        if (responseData.shift_alerts[0]?.latest_notification) {
          const transformed = responseData.shift_alerts.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.shift_alerts)
        }
      }

      if (responseData.requires_action?.length > 0) {
        if (responseData.requires_action[0]?.latest_notification) {
          const transformed = responseData.requires_action.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.requires_action)
        }
      }

      if (responseData.informational?.length > 0) {
        if (responseData.informational[0]?.latest_notification) {
          const transformed = responseData.informational.map(
            transformThreadLikeToNotification
          )
          allNotifications.push(...transformed)
        } else {
          allNotifications.push(...responseData.informational)
        }
      }

      return allNotifications
    }

    if (isArchiveResponse(data)) {
      return data.data.notifications ?? []
    }

    return []
  }, [data])

  const teamFeedCount = useMemo(() => {
    if (!data?.data || !isTeamFeedResponse(data)) return 0

    // Get notifications and group them
    const notifications =
      data.data.threads?.map(transformThreadLikeToNotification) ?? []
    const grouped = groupNotifications(notifications)

    return grouped.filter(g => g.hasUnread).length
  }, [data])

  const myWorkCount = useMemo(() => {
    if (!data?.data || !isMyWorkResponse(data)) return 0

    const responseData = data.data as any
    const allNotifications: NotificationSummary[] = []

    if (responseData.active_cases?.length > 0) {
      if (responseData.active_cases[0]?.latest_notification) {
        allNotifications.push(
          ...responseData.active_cases.map(transformThreadLikeToNotification)
        )
      } else {
        allNotifications.push(...responseData.active_cases)
      }
    }

    if (responseData.shift_alerts?.length > 0) {
      if (responseData.shift_alerts[0]?.latest_notification) {
        allNotifications.push(
          ...responseData.shift_alerts.map(transformThreadLikeToNotification)
        )
      } else {
        allNotifications.push(...responseData.shift_alerts)
      }
    }

    const grouped = groupNotifications(allNotifications)
    return grouped.filter(g => g.hasUnread).length
  }, [data])

  const archiveCount = useMemo(() => {
    if (!data?.data || !isArchiveResponse(data)) return 0

    const notifications = data.data.notifications ?? []
    const grouped = groupNotifications(notifications)

    return grouped.length
  }, [data])

  useEffect(() => {
    const grouped = groupNotifications(notifications)
    setGroupedNotifications(prev => {
      const deferredMap = new Map()
      prev.forEach(g => {
        if (g.isDeferred) {
          deferredMap.set(g.data_id, true)
        }
      })
      return grouped.map(g => ({
        ...g,
        isDeferred: deferredMap.get(g.data_id) || g.isDeferred || false,
      }))
    })
  }, [notifications])

  const filteredNotifications = groupedNotificationsState.filter(group => {
    if (!search.trim()) return true
    const searchLower = search.toLowerCase()
    return group.notifications.some(n =>
      n.updated_by_name?.toLowerCase().includes(searchLower)
    )
  })

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const handleNotificationInView = useCallback(
    (group: GroupedNotification) => {
      const latest = group.notifications[0]
      if (!latest) return

      const notificationWithData = latest as any
      const notificationId = notificationWithData.notification_id || latest.id
      const version = notificationWithData.version || 1
      const requestKey = `${notificationId}-${version}`

      if (
        globalSeenNotifications.has(notificationId) ||
        globalSeenInProgress.has(requestKey)
      ) {
        return
      }

      markAsSeenMutation.mutate(
        {
          notificationId,
          version,
          entity: 'dispatcher',
        },
        {
          onSuccess: () => {
            globalSeenNotifications.add(notificationId)
            globalSeenInProgress.delete(requestKey)
            setGroupedNotifications(prev =>
              prev.map(g => {
                if (g.data_id === group.data_id) {
                  return {
                    ...g,
                    hasUnread: false,
                    isSeen: true,
                    notifications: g.notifications.map(n => ({
                      ...n,
                      status: 'read' as const,
                    })),
                  }
                }
                return g
              })
            )
          },
          onError: () => {
            globalSeenInProgress.delete(requestKey)
          },
        }
      )
    },
    [markAsSeenMutation]
  )

  const markGroupAsRead = useCallback(
    async (group: GroupedNotification, notification: NotificationSummary) => {
      const notificationId =
        (notification as any).notification_id || notification.id
      const version = (notification as any).version || 1
      const operationKey = `archive-${notificationId}`

      if (processingRef.current.has(operationKey)) {
        return
      }

      processingRef.current.add(operationKey)
      setMarkingAsRead(prev => ({ ...prev, [group.data_id]: true }))

      try {
        await archiveMutation.mutateAsync({
          notificationId,
          version,
          entity: 'dispatcher',
        })

        setGroupedNotifications(
          prev =>
            prev
              .map(g => {
                if (g.data_id === group.data_id) {
                  const updatedNotifications = g.notifications.filter(
                    n => n.id !== notification.id
                  )

                  if (updatedNotifications.length === 0) {
                    return null
                  }

                  const hasUnread = updatedNotifications.some(
                    n => n.status === 'unread'
                  )
                  const isSeen = updatedNotifications.every(
                    n => n.status === 'read'
                  )

                  return {
                    ...g,
                    notifications: updatedNotifications,
                    hasUnread,
                    isSeen,
                  }
                }
                return g
              })
              .filter(Boolean) as GroupedNotification[]
        )

        toast.success('Notification archived successfully')
        await refetch()
        onRefresh()
      } catch (error: any) {
        if (error?.response?.data?.status === 'conflict') {
          toast.error('Notification was updated elsewhere. Please refresh.')
          await refetch()
          onRefresh()
        } else if (error?.response?.status === 404) {
          toast.error(
            'Notification not found. It may have been already archived.'
          )
        } else {
          toast.error(error?.message || 'Failed to archive notification')
        }
      } finally {
        setMarkingAsRead(prev => ({ ...prev, [group.data_id]: false }))
        processingRef.current.delete(operationKey)
      }
    },
    [archiveMutation, refetch, onRefresh]
  )

  const viewCaseDetails = useCallback(
    async (group: GroupedNotification) => {
      const caseId = group.data_id

      const operationKey = `view-${caseId}`

      if (processingRef.current.has(operationKey)) {
        return
      }

      processingRef.current.add(operationKey)

      try {
        if (group.hasUnread) {
          const latest = group.notifications[0]
          if (latest) {
            const notificationWithData = latest as any
            const notificationId =
              notificationWithData.notification_id || latest.id

            setGroupedNotifications(prev =>
              prev.map(g => {
                if (g.data_id === group.data_id) {
                  return {
                    ...g,
                    hasUnread: false,
                    isSeen: true,
                    notifications: g.notifications.map(n => ({
                      ...n,
                      status: 'read' as const,
                      action: 'seen',
                    })),
                  }
                }
                return g
              })
            )

            globalSeenNotifications.add(notificationId)
          }
        }

        onClose()

        setTimeout(() => {
          const latest = group.notifications[0] as any

          const dataType = latest?.data_type
          const shiftCase =
            dataType === 'shift' ||
            caseId.startsWith('shift-') ||
            latest?.case_id?.startsWith('shift-') ||
            latest?.data_id?.startsWith('shift-')

          if (shiftCase) {
            navigate('/shift-management', {
              state: {
                activeTab: 'My Shift',
              },
            })
            return
          }

          if (caseId.startsWith('cas-')) {
            navigate(`/cases/${caseId}`)
            return
          }

          if (caseId.startsWith('ar-')) {
            navigate('/ambulance-request')
            return
          }

          navigate('/cases')
        }, 100)
      } catch (error: any) {
        if (error?.response?.data?.status === 'conflict') {
          toast.error('Notification was updated elsewhere. Refreshing...')
          await refetch()
        } else {
          toast.error('Failed to view case details')
        }
      } finally {
        processingRef.current.delete(operationKey)
      }
    },
    [navigate, onClose, refetch, markAsReadMutation]
  )

  const renderEmptyState = () => {
    const messages = {
      archive: {
        title: 'No Archived Notifications',
        description: 'Resolved cases and archived items will appear here.',
      },
      team_feed: {
        title: 'No Team Feed Notifications',
        description: 'Team activities will appear here.',
      },
      my_work: {
        title: 'Nothing Assigned Yet',
        description: 'Cases that require your attention will appear here.',
      },
    }

    const message = messages[activeTab]

    return (
      <div className="h-full flex flex-col items-center justify-center text-center">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Text size="xl">📁</Text>
        </div>
        <Text fw={600} size="sm">
          {message.title}
        </Text>
        <Text size="xs" c="dimmed">
          {message.description}
        </Text>
      </div>
    )
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
      setIsClosing(false)
    }, 300)
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-[90]" onClick={handleClose} />

      <div
        className={`fixed top-0 right-0 z-[100] h-full w-[600px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          isClosing ? 'translate-x-full' : 'translate-x-0'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 h-20 border-b border-[#E8E8E8] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold leading-none text-[#2E2E2E]">
              Notifications
            </h1>

            <Bell size={20} className="-mt-1 text-[#8C8C8C]" strokeWidth={2} />
          </div>

          <button
            onClick={handleClose}
            className="rounded-md p-1 hover:bg-gray-100 transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-3 pt-4 flex-shrink-0">
          <div className="grid grid-cols-3 bg-[#F4F4F6] p-1 rounded-xl">
            {[
              { key: 'team_feed', label: 'Team Feed', count: teamFeedCount },
              { key: 'my_work', label: 'My Work', count: myWorkCount },
              { key: 'archive', label: 'Archive', count: archiveCount },
            ].map(tab => {
              const isActive = activeTab === tab.key
              const isTabLoading = isFetching && isActive

              return (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as NotificationView)
                    setExpandedGroups({})
                    refetch()
                  }}
                  className={`h-[54px] rounded-xl text-[15px] transition-colors cursor-pointer relative
          ${
            isActive
              ? 'bg-white border border-[#CFCFD4] text-[#24476B] font-semibold'
              : 'text-[#2C2C2C] hover:bg-[#EAEAEA]'
          }`}
                  disabled={isTabLoading}
                >
                  <div className="flex w-full items-center justify-between px-3">
                    <span>{tab.label}</span>

                    {isTabLoading ? (
                      <div className="flex items-center justify-center min-w-[26px]">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#24476B] border-t-transparent" />
                      </div>
                    ) : (
                      tab.count > 0 && (
                        <span className="flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full bg-[#FA5252] text-[12px] font-bold text-white">
                          {tab.count}
                        </span>
                      )
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-4 flex-shrink-0">
          <div className="flex gap-3">
            <div className="flex-1 h-[56px] border border-[#A4A4A4] rounded-xl px-4 flex items-center gap-3">
              <Search size={20} className="text-[#8C8C8C]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name..."
                className="flex-1 outline-none text-sm bg-transparent"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="text-[#8C8C8C] hover:text-[#2E2E2E] cursor-pointer"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content - MODIFIED FOR PAGINATION */}
        <div className="flex-1 overflow-y-auto px-3 pb-5">
          {isLoading || initialLoading ? (
            <NotificationsModalSkeleton />
          ) : filteredNotifications.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              {filteredNotifications.map((group, index) => {
                const isExpanded = expandedGroups[group.data_id] || false
                const isMarkingRead = markingAsRead[group.data_id] || false
                const isDeferred = group.isDeferred || false
                const isSeen = group.isSeen || !group.hasUnread
                const isProcessing = processingGroups[group.data_id] || false

                // ADDED FOR PAGINATION: Check if this is the last element
                const isLastElement = index === filteredNotifications.length - 1

                return (
                  <div
                    key={group.data_id}
                    ref={isLastElement ? lastElementRef : null}
                  >
                    <NotificationItem
                      group={group}
                      isExpanded={isExpanded}
                      isMarkingRead={isMarkingRead}
                      isDeferred={isDeferred}
                      isSeen={isSeen}
                      isProcessing={isProcessing}
                      onToggleExpand={toggleGroupExpansion}
                      onArchive={markGroupAsRead}
                      onViewDetails={viewCaseDetails}
                      onInView={handleNotificationInView}
                      activeTab={activeTab}
                    />
                  </div>
                )
              })}

              {/* ADDED FOR PAGINATION: Loading indicator for more items */}
              {isFetching && lastKey !== null && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#24476B]"></div>
                </div>
              )}

              {/* ADDED FOR PAGINATION: Show message when all items are loaded */}
              {!hasMore && filteredNotifications.length > 0 && (
                <div className="text-center py-4 text-sm text-[#7A7A7A]">
                  All notifications loaded
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default NotificationsModal
